import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine
} from 'recharts';

const SERIES_1 = '#2a78d6';
const SERIES_2 = '#eb6834';
const SERIES_3 = '#1baf7a';
const GRIDLINE = '#e1e0d9';
const MUTED = '#898781';
const HIGHLIGHT_BG = 'rgba(235, 104, 52, 0.18)';

function formatDate(ts) {
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateTime(ts) {
  const d = new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
  });
}

function CustomTooltip({ active, payload, bblPerIn }) {
  if (!active || !payload || !payload.length) return null;
  const pt = payload[0]?.payload;
  if (!pt) return null;

  return (
    <div className="custom-tooltip">
      <div className="tt-time">{formatDateTime(pt.ts)}</div>
      <div className="tt-value">
        Level: {pt.level?.toFixed(1)} in
        {bblPerIn > 0 && ` (${(pt.level * bblPerIn).toFixed(1)} bbl)`}
      </div>
      {pt.preFit != null && (
        <div style={{ color: SERIES_3, marginTop: 2, fontWeight: 500 }}>
          Pre-haul fit: {pt.preFit.toFixed(1)} in
        </div>
      )}
      {pt.postFit != null && (
        <div style={{ color: SERIES_2, marginTop: 2, fontWeight: 500 }}>
          Post-haul fit: {pt.postFit.toFixed(1)} in
        </div>
      )}
    </div>
  );
}

function CalcPanel({ h, fmtVol, bblPerIn }) {
  const verticalDiff = h.haul_volume_ft; // b1 - b2 (difference of line constants at drop)
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`calc-panel${expanded ? ' expanded' : ''}`}>
      <button
        type="button"
        className="calc-title calc-toggle"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
      >
        <span className={`calc-chevron${expanded ? ' open' : ''}`}>▸</span>
        How this haul volume is calculated
      </button>
      {expanded && (
      <>
      <div className="calc-steps">
        <div className="calc-step">
          <div className="calc-step-num" style={{ background: SERIES_3 }}>1</div>
          <div className="calc-step-body">
            <div className="calc-step-label">Fit trend line to tank level <strong>before</strong> the drop</div>
            <div className="calc-step-detail">
              Linear fit over {formatDateTime(h.pre_fit_range[0])} — {formatDateTime(h.pre_fit_range[1])}
              <span className="calc-meta">
                slope: {h.pre_slope_ft_per_hr >= 0 ? '+' : ''}{h.pre_slope_ft_per_hr.toFixed(2)} in/hr
                {' · '}R² = {isFinite(h.pre_r_squared) ? h.pre_r_squared.toFixed(2) : '—'}
              </span>
            </div>
          </div>
        </div>
        <div className="calc-step">
          <div className="calc-step-num" style={{ background: SERIES_2 }}>2</div>
          <div className="calc-step-body">
            <div className="calc-step-label">Fit trend line to tank level <strong>after</strong> the drop</div>
            <div className="calc-step-detail">
              Linear fit over {formatDateTime(h.post_fit_range[0])} — {formatDateTime(h.post_fit_range[1])}
              <span className="calc-meta">
                slope: {h.post_slope_ft_per_hr >= 0 ? '+' : ''}{h.post_slope_ft_per_hr.toFixed(2)} in/hr
                {' · '}R² = {isFinite(h.post_r_squared) ? h.post_r_squared.toFixed(2) : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="calc-methods">
        <div className="calc-method-card">
          <div className="calc-method-label">Volume = difference of the line constants</div>
          <div className="calc-method-desc">
            Both lines use x = 0 at the drop, so each line's constant term is its
            level at the drop instant. Subtract the constants:
          </div>
          <div className="calc-method-math">
            pre-line: y = {h.pre_fit_slope_per_min.toFixed(4)}·x{' '}
            <span style={{ color: SERIES_3, fontWeight: 600 }}>+ {h.pre_fit_intercept.toFixed(1)}</span>
          </div>
          <div className="calc-method-math">
            post-line: y = {h.post_fit_slope_per_min.toFixed(4)}·x{' '}
            <span style={{ color: SERIES_2, fontWeight: 600 }}>+ {h.post_fit_intercept.toFixed(1)}</span>
          </div>
          <div className="calc-method-math" style={{ marginTop: 6 }}>
            drop ={' '}
            <span style={{ color: SERIES_3, fontWeight: 600 }}>{h.pre_fit_intercept.toFixed(1)}</span>
            {' − '}
            <span style={{ color: SERIES_2, fontWeight: 600 }}>{h.post_fit_intercept.toFixed(1)}</span>
            {' = '}
            <span className="calc-result">{fmtVol(verticalDiff)}</span>
          </div>
        </div>
      </div>

      <div className="calc-why">
        Volume is taken from the fitted trend lines (not the raw noisy readings at
        the transition): the change in the constant term between the pre-haul and
        post-haul line equals the level drawn down, converted to barrels.
      </div>
      </>
      )}
    </div>
  );
}

const TIMELINE_H = 52;
const HANDLE_W = 7;
const TL_PAD_L = 52;
const TL_PAD_R = 16;

function TimelineNav({ timeSeries, viewWindow, onViewChange }) {
  const containerRef = useRef(null);
  const [cw, setCw] = useState(0);
  const interRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(([e]) => setCw(e.contentRect.width));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const allPts = useMemo(() =>
    timeSeries.map(pt => ({ ts: new Date(pt.time).getTime(), level: pt.level })),
    [timeSeries]
  );

  const dMin = allPts.length > 0 ? allPts[0].ts : 0;
  const dMax = allPts.length > 0 ? allPts[allPts.length - 1].ts : 1;
  const dSpan = dMax - dMin || 1;
  const plotW = Math.max(1, cw - TL_PAD_L - TL_PAD_R);

  const tsToX = useCallback(ts => TL_PAD_L + ((ts - dMin) / dSpan) * plotW, [dMin, dSpan, plotW]);
  const xToTs = useCallback(x => dMin + ((x - TL_PAD_L) / plotW) * dSpan, [dMin, dSpan, plotW]);

  const sparkPath = useMemo(() => {
    if (allPts.length < 2 || plotW <= 0) return '';
    const levels = allPts.map(p => p.level).filter(v => v != null && isFinite(v));
    if (!levels.length) return '';
    const lo = Math.min(...levels), hi = Math.max(...levels), rng = hi - lo || 1;
    const step = Math.max(1, Math.floor(allPts.length / (plotW / 2)));
    let pts = '';
    for (let i = 0; i < allPts.length; i += step) {
      const p = allPts[i];
      if (p.level == null || !isFinite(p.level)) continue;
      const x = tsToX(p.ts);
      const y = TIMELINE_H - 18 - ((p.level - lo) / rng) * (TIMELINE_H - 26);
      pts += pts ? ` L${x},${y}` : `M${x},${y}`;
    }
    const last = allPts[allPts.length - 1], first = allPts[0];
    return pts + ` L${tsToX(last.ts)},${TIMELINE_H - 16} L${tsToX(first.ts)},${TIMELINE_H - 16} Z`;
  }, [allPts, plotW, tsToX]);

  const clamp = useCallback((s, e) => {
    let a = Math.max(dMin, Math.min(s, dMax));
    let b = Math.max(dMin, Math.min(e, dMax));
    const minW = dSpan * 0.005;
    if (b - a < minW) b = Math.min(a + minW, dMax);
    return { start: a, end: b };
  }, [dMin, dMax, dSpan]);

  const selL = viewWindow ? tsToX(viewWindow.start) : TL_PAD_L;
  const selR = viewWindow ? tsToX(viewWindow.end) : TL_PAD_L + plotW;
  const isZoomed = viewWindow != null;

  const onDown = useCallback((e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    let mode;
    if (isZoomed && Math.abs(x - selL) < HANDLE_W + 2) mode = 'left';
    else if (isZoomed && Math.abs(x - selR) < HANDLE_W + 2) mode = 'right';
    else if (isZoomed && x > selL && x < selR) mode = 'pan';
    else mode = 'create';
    interRef.current = { mode, startX: x, startTs: xToTs(x), startWin: viewWindow ? { ...viewWindow } : null };
    e.preventDefault();
  }, [isZoomed, selL, selR, viewWindow, xToTs]);

  useEffect(() => {
    const onMove = (e) => {
      const ir = interRef.current;
      if (!ir || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const ts = xToTs(x);
      const { mode, startTs, startWin } = ir;
      if (mode === 'pan' && startWin) {
        const d = ts - startTs;
        let s = startWin.start + d, en = startWin.end + d;
        const w = startWin.end - startWin.start;
        if (s < dMin) { s = dMin; en = dMin + w; }
        if (en > dMax) { en = dMax; s = dMax - w; }
        onViewChange({ start: s, end: en });
      } else if (mode === 'left' && startWin) {
        onViewChange(clamp(ts, startWin.end));
      } else if (mode === 'right' && startWin) {
        onViewChange(clamp(startWin.start, ts));
      } else if (mode === 'create') {
        const a = Math.min(startTs, ts), b = Math.max(startTs, ts);
        if (b - a > dSpan * 0.005) onViewChange(clamp(a, b));
      }
    };
    const onUp = () => { interRef.current = null; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [xToTs, onViewChange, clamp, dMin, dMax, dSpan]);

  const onDblClick = useCallback(() => onViewChange(null), [onViewChange]);

  if (allPts.length < 2) return null;

  const midTick = dMin + dSpan / 2;

  return (
    <div
      ref={containerRef}
      className="timeline-nav"
      onMouseDown={onDown}
      onDoubleClick={onDblClick}
    >
      {cw > 0 && (
        <>
          <svg width={cw} height={TIMELINE_H} className="timeline-svg">
            <path d={sparkPath} fill={SERIES_1} fillOpacity={0.12} stroke={SERIES_1} strokeWidth={1} strokeOpacity={0.3} />
          </svg>

          {isZoomed && (
            <>
              <div className="tl-dim" style={{ left: TL_PAD_L, width: Math.max(0, selL - TL_PAD_L) }} />
              <div className="tl-dim" style={{ left: selR, width: Math.max(0, TL_PAD_L + plotW - selR) }} />
              <div className="tl-sel" style={{ left: selL, width: Math.max(4, selR - selL) }}>
                <div className="tl-handle tl-handle-l" />
                <div className="tl-handle tl-handle-r" />
              </div>
            </>
          )}

          <div className="tl-labels">
            <span style={{ left: TL_PAD_L }}>{formatDate(dMin)}</span>
            <span style={{ left: tsToX(midTick), transform: 'translateX(-50%)' }}>{formatDate(midTick)}</span>
            <span style={{ right: TL_PAD_R }}>{formatDate(dMax)}</span>
          </div>
        </>
      )}
    </div>
  );
}

function TankChart({ timeSeries, hauls, bblPerIn, highlightedHaul, onHaulClick }) {
  const [viewWindow, setViewWindow] = useState(null);
  const [zoomStart, setZoomStart] = useState(null);
  const [zoomEnd, setZoomEnd] = useState(null);
  const chartRef = useRef(null);
  const dragRef = useRef({ start: null, end: null, dragging: false });

  const resetZoom = useCallback(() => {
    setViewWindow(null);
  }, []);

  const finalizeDrag = useCallback(() => {
    const { start, end, dragging } = dragRef.current;
    dragRef.current = { start: null, end: null, dragging: false };
    if (dragging && start != null && end != null && start !== end) {
      const s = Math.min(start, end);
      const e = Math.max(start, end);
      setViewWindow({ start: s, end: e });
    }
    setZoomStart(null);
    setZoomEnd(null);
  }, []);

  useEffect(() => {
    const onGlobalMouseUp = () => {
      if (dragRef.current.dragging) finalizeDrag();
    };
    document.addEventListener('mouseup', onGlobalMouseUp);
    return () => document.removeEventListener('mouseup', onGlobalMouseUp);
  }, [finalizeDrag]);

  const handleMouseDown = useCallback((e) => {
    if (e && e.activeLabel != null) {
      dragRef.current = { start: e.activeLabel, end: e.activeLabel, dragging: true };
      setZoomStart(e.activeLabel);
      setZoomEnd(e.activeLabel);
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (dragRef.current.dragging && e && e.activeLabel != null) {
      dragRef.current.end = e.activeLabel;
      setZoomEnd(e.activeLabel);
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    if (dragRef.current.dragging) finalizeDrag();
  }, [finalizeDrag]);

  const chartData = useMemo(() => {
    let points = timeSeries.map(pt => ({ ...pt, ts: new Date(pt.time).getTime() }));

    // Filter to view window
    if (viewWindow) {
      points = points.filter(pt => pt.ts >= viewWindow.start && pt.ts <= viewWindow.end);
    }

    // Add fitted lines if a haul is highlighted
    if (highlightedHaul != null && hauls[highlightedHaul]) {
      const h = hauls[highlightedHaul];
      const refTime = new Date(h.ref_time).getTime();

      points = points.map(pt => {
        const minutesFromRef = (pt.ts - refTime) / 60000;
        const result = { ...pt };

        const preStart = new Date(h.pre_fit_range[0]).getTime();
        const preEnd = new Date(h.drop_start).getTime();
        if (pt.ts >= preStart && pt.ts <= preEnd) {
          result.preFit = h.pre_fit_intercept + h.pre_fit_slope_per_min * minutesFromRef;
        }

        const postStart = new Date(h.drop_end).getTime();
        const postEnd = new Date(h.post_fit_range[1]).getTime();
        if (pt.ts >= postStart && pt.ts <= postEnd) {
          result.postFit = h.post_fit_intercept + h.post_fit_slope_per_min * minutesFromRef;
        }

        return result;
      });
    }

    return points;
  }, [timeSeries, hauls, highlightedHaul, viewWindow]);

  const yDomain = useMemo(() => {
    const levels = chartData.map(d => d.level).filter(v => v != null);
    if (levels.length === 0) return [0, 100];
    const min = Math.floor(Math.min(...levels));
    const max = Math.ceil(Math.max(...levels));
    const pad = Math.max((max - min) * 0.05, 2);
    return [Math.max(0, min - pad), max + pad];
  }, [chartData]);

  // Only render haul bands that fall within the current view
  const visibleHauls = useMemo(() => {
    const mapped = hauls.map((h, i) => ({
      ...h,
      _idx: i,
      _startTs: new Date(h.drop_start).getTime(),
      _endTs: new Date(h.drop_end).getTime(),
    }));
    if (!viewWindow) return mapped;
    return mapped.filter(h => h._startTs >= viewWindow.start && h._startTs <= viewWindow.end);
  }, [hauls, viewWindow]);

  const timeSpanMs = useMemo(() => {
    if (chartData.length < 2) return 1;
    return chartData[chartData.length - 1].ts - chartData[0].ts;
  }, [chartData]);

  const selectedHaul = highlightedHaul != null ? hauls[highlightedHaul] : null;

  const fmtVol = (ft) => {
    if (bblPerIn > 0) return `${(ft * bblPerIn).toFixed(1)} bbl`;
    return `${ft.toFixed(1)} in`;
  };

  return (
    <div className="chart-card" ref={chartRef}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <h2>Tank Level Over Time</h2>
          <div className="chart-subtitle">
            {hauls.length} haul events
            {highlightedHaul != null && selectedHaul && (
              <span>
                {' — '}selected: <strong>{fmtVol(selectedHaul.haul_volume_ft)}</strong> drop
                at {formatDateTime(selectedHaul.drop_start)}
              </span>
            )}
          </div>
        </div>
        {viewWindow && (
          <button className="quick-btn" onClick={resetZoom} style={{ marginBottom: 4 }}>
            Show full range
          </button>
        )}
      </div>

      <div className="chart-legend">
        <div className="legend-item">
          <div className="legend-swatch" style={{ background: SERIES_1 }} />
          <span>Tank level</span>
        </div>
        {highlightedHaul != null && (
          <>
            <div className="legend-item">
              <div className="legend-swatch" style={{ background: SERIES_3 }} />
              <span>Pre-haul trend line</span>
            </div>
            <div className="legend-item">
              <div className="legend-swatch" style={{ background: SERIES_2 }} />
              <span>Post-haul trend line</span>
            </div>
          </>
        )}
        <div className="legend-item">
          <div className="legend-swatch dot" style={{ background: SERIES_2, opacity: 0.2 }} />
          <span>Haul event</span>
        </div>
      </div>

      {/* Calculation breakdown panel */}
      {selectedHaul && <CalcPanel h={selectedHaul} fmtVol={fmtVol} bblPerIn={bblPerIn} />}

      <ResponsiveContainer width="100%" height={380}>
        <ComposedChart
          data={chartData}
          margin={{ top: 8, right: 16, bottom: 4, left: 0 }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          style={{ cursor: zoomStart != null ? 'col-resize' : 'crosshair', userSelect: 'none' }}
        >
          <CartesianGrid stroke={GRIDLINE} strokeWidth={1} vertical={false} />
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tickFormatter={viewWindow ? formatDateTime : formatDate}
            tick={{ fontSize: 11, fill: MUTED }}
            stroke={GRIDLINE}
            tickLine={false}
          />
          <YAxis
            domain={yDomain}
            tick={{ fontSize: 11, fill: MUTED }}
            stroke={GRIDLINE}
            tickLine={false}
            label={{
              value: 'Level (in)',
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: 11, fill: MUTED },
              offset: 10
            }}
          />
          <Tooltip content={<CustomTooltip bblPerIn={bblPerIn} />} />

          {/* Haul bands — all have minimum width so they're visible & clickable */}
          {visibleHauls.map((h) => {
            const isSelected = highlightedHaul === h._idx;
            const minHalfMs = timeSpanMs * 0.004;
            const mid = (h._startTs + h._endTs) / 2;
            const halfSpan = Math.max((h._endTs - h._startTs) / 2, minHalfMs);
            return (
              <ReferenceArea
                key={h._idx}
                x1={mid - halfSpan}
                x2={mid + halfSpan}
                fill={isSelected ? SERIES_1 : SERIES_2}
                fillOpacity={isSelected ? 0.25 : 0.12}
                stroke={isSelected ? SERIES_1 : SERIES_2}
                strokeWidth={isSelected ? 2 : 1}
                strokeOpacity={isSelected ? 1 : 0.3}
                ifOverflow="extendDomain"
                onClick={() => onHaulClick(isSelected ? null : h._idx)}
                style={{ cursor: 'pointer' }}
              />
            );
          })}

          {/* Vertical lines at the drop boundaries for selected haul */}
          {selectedHaul && (
            <>
              <ReferenceLine
                x={new Date(selectedHaul.drop_start).getTime()}
                stroke={SERIES_1}
                strokeWidth={2}
                ifOverflow="extendDomain"
                label={{ value: `${selectedHaul.level_before_ft.toFixed(1)} in`, position: 'top', fontSize: 11, fill: SERIES_1 }}
              />
              <ReferenceLine
                x={new Date(selectedHaul.drop_end).getTime()}
                stroke={SERIES_1}
                strokeWidth={2}
                ifOverflow="extendDomain"
                label={{ value: `${selectedHaul.level_after_ft.toFixed(1)} in`, position: 'top', fontSize: 11, fill: SERIES_1 }}
              />
              {/* Horizontal lines at before/after levels */}
              <ReferenceLine
                y={selectedHaul.level_before_ft}
                stroke={SERIES_3}
                strokeDasharray="3 4"
                strokeWidth={1}
                strokeOpacity={0.6}
              />
              <ReferenceLine
                y={selectedHaul.level_after_ft}
                stroke={SERIES_2}
                strokeDasharray="3 4"
                strokeWidth={1}
                strokeOpacity={0.6}
              />
            </>
          )}

          {/* Tank level line */}
          <Line
            type="monotone"
            dataKey="level"
            stroke={SERIES_1}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />

          {/* Fitted lines when haul is selected */}
          {highlightedHaul != null && (
            <>
              <Line
                type="monotone"
                dataKey="preFit"
                stroke={SERIES_3}
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="postFit"
                stroke={SERIES_2}
                strokeWidth={2}
                strokeDasharray="6 3"
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
            </>
          )}

          {/* Drag-to-zoom selection preview */}
          {zoomStart != null && zoomEnd != null && (
            <ReferenceArea
              x1={Math.min(zoomStart, zoomEnd)}
              x2={Math.max(zoomStart, zoomEnd)}
              fill="#666"
              fillOpacity={0.15}
              stroke="#666"
              strokeWidth={1}
              strokeDasharray="4 2"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
      <TimelineNav
        timeSeries={timeSeries}
        viewWindow={viewWindow}
        onViewChange={setViewWindow}
      />
      <div className="zoom-hint">
        Drag the timeline to pan. Drag edges to resize. Double-click to reset.
      </div>
    </div>
  );
}

export default TankChart;
